import { useTheme } from "@/theme/ThemeProvider";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import {
  FINDER_NOTES_HELP,
  FINDER_NOTES_LABEL,
  type FinderDraft,
} from "@/ui/finder/finderMode";
import { useRef, type Ref } from "react";
import { Text, TextInput, View } from "react-native";

export function FinderDraftFields({
  draft,
  onChange,
  nameHint,
  nameInputRef,
  onNotesSubmitEditing,
}: {
  draft: FinderDraft;
  onChange: (patch: Partial<FinderDraft>) => void;
  nameHint?: string | null;
  nameInputRef?: Ref<TextInput>;
  onNotesSubmitEditing?: () => void;
}) {
  const { theme } = useTheme();
  const phoneRef = useRef<TextInput>(null);
  const emailRef = useRef<TextInput>(null);
  const addressRef = useRef<TextInput>(null);
  const notesRef = useRef<TextInput>(null);

  return (
    <View style={{ gap: theme.space.sm }}>
      <FloatingLabelInput
        ref={nameInputRef}
        label="Ihr Name"
        accessibilityLabel="Ihr Name, Pflichtfeld"
        value={draft.displayName}
        onChangeText={(displayName) => onChange({ displayName })}
        autoCapitalize="words"
        required
        returnKeyType="next"
        blurOnSubmit={false}
        onSubmitEditing={() => phoneRef.current?.focus()}
      />
      {nameHint ? (
        <Text
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
          style={{ color: theme.color.dangerText, fontSize: theme.fontSize.body }}
        >
          {nameHint}
        </Text>
      ) : null}
      <FloatingLabelInput
        ref={phoneRef}
        label="Telefon (optional)"
        accessibilityLabel="Telefon, optional"
        value={draft.phone}
        onChangeText={(phone) => onChange({ phone })}
        keyboardType="phone-pad"
        returnKeyType="next"
        blurOnSubmit={false}
        onSubmitEditing={() => emailRef.current?.focus()}
      />
      <FloatingLabelInput
        ref={emailRef}
        label="E-Mail (optional)"
        accessibilityLabel="E-Mail, optional"
        value={draft.email}
        onChangeText={(email) => onChange({ email })}
        keyboardType="email-address"
        autoCapitalize="none"
        returnKeyType="next"
        blurOnSubmit={false}
        onSubmitEditing={() => addressRef.current?.focus()}
      />
      <FloatingLabelInput
        ref={addressRef}
        label="Anschrift (optional)"
        accessibilityLabel="Anschrift, optional"
        value={draft.address}
        onChangeText={(address) => onChange({ address })}
        returnKeyType="next"
        blurOnSubmit={false}
        onSubmitEditing={() => notesRef.current?.focus()}
      />
      <View style={{ gap: theme.space.xs }}>
        <FloatingLabelInput
          ref={notesRef}
          label={FINDER_NOTES_LABEL}
          accessibilityLabel={`${FINDER_NOTES_LABEL}, optional`}
          value={draft.notes}
          onChangeText={(notes) => onChange({ notes })}
          returnKeyType={onNotesSubmitEditing ? "next" : "done"}
          blurOnSubmit={!onNotesSubmitEditing}
          onSubmitEditing={onNotesSubmitEditing}
        />
        <Text
          style={{
            color: theme.color.textSecondary,
            fontSize: theme.fontSize.small,
          }}
        >
          {FINDER_NOTES_HELP}
        </Text>
      </View>
      <Text style={{ color: theme.color.textSecondary, fontSize: theme.fontSize.small }}>
        * Pflichtfeld
      </Text>
    </View>
  );
}
